import Mustache from 'mustache';
import Questions from './components/Questions';
import { ArticleShow, ArticleSidebar, ArticlesService } from '@atreus/data';
import { Aside } from '../components/Aside';
import { Badges } from '../../../components/Badges';
import { Box, Container, Stack, Text, useMounted } from '@qcx/ui';
import { Breadcrumb, BreadcrumbLink } from '../../../components/Breadcrumb';
import { LikeDislikeButtons } from './components/LikeDislikeButtons';
import { NewsStoreProvider } from '../news.store';
import { Page } from '../../../components/page/Page';
import { PageBelowTheFoldComponent } from './components/PageBelowTheFoldComponent';
// eslint-disable-next-line import/no-unresolved
import { Route } from './+types/index';
import { SocialShareButtons } from '../components/SocialShareButtons';
import { Suspense, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { data } from 'react-router';
import { generateCanonicalUrl } from '../../../lib/generate-canonical-url.lib';
import { getCommentaries } from './news.service';
import { getFirstWords } from '../../../lib/get-first-words.lib';
import { safeRenderHtml } from '../../../lib/safe-render-html.lib';
import { splitContentByParagraphs } from '../../../lib/split-content-by-paragrphs.lib';

const getDefaultHref = (baseUrl: string): string => `${baseUrl}/feed`;

const getEditorialHref = (baseUrl: string, editorialSlug: string): string =>
  `${baseUrl}/${editorialSlug}/feed`;

const hasEditorials = (data: ArticleShow): boolean =>
  Boolean(data?.editorials?.length);

const extractAndTransformShortcodes = (content: string) => {
  const patterns: Record<string, RegExp> = {
    Q: /{{{Q\d+(?:, Q\d+)*(?:, [\dA-Za-z-]+)*}}}/g,
    HS: /{{{HS\d+}}}/g
  };

  const templates: Record<string, (value?: string) => string> = {
    Q: () => '{{{#question}}}{{{/question}}}',
    HS: (id?: string) => `{{{#hubspot}}}${id as string}{{{/hubspot}}}`
  };

  const matches: Record<string, string[]> = {
    Q: [],
    HS: []
  };

  for (const type in patterns) {
    const regex = patterns[type];
    const foundMatches = content.match(regex);

    if (foundMatches) {
      matches[type] = foundMatches;
    }
  }

  return content
    .replace(matches.Q[0], templates['Q']())
    .replace(
      matches.HS[0],
      templates['HS'](matches?.HS?.[0]?.replaceAll(/\D+/g, ''))
    );
};

const addingShortcodeContent = (content: string) => {
  return Mustache.render(
    extractAndTransformShortcodes(content),
    {
      // eslint-disable-next-line unicorn/consistent-function-scoping
      question: () => () => '<div id="question"></div>',
      // eslint-disable-next-line unicorn/consistent-function-scoping
      hubspot: () => (id: string) => `
        <div class="hs-cta-embed hs-cta-embed-${id}" data-hubspot-wrapper-cta-id="${id}"></div>
        <img style="visibility:hidden;"
          src="https://no-cache.hubspot.com/cta/default/44326808/interactive-${id}.png"
          onerror="document.querySelector('.hs-cta-embed.hs-cta-embed-${id}').remove();this.remove()"
          onload="this.remove();" />
      `
    },
    {},
    ['{{{', '}}}']
  );
};

export const meta = ({ data }: Route.MetaArgs) => {
  if (!data) {
    return [];
  }

  const baseUrl = data.baseUrl;
  const canonicalUrl = generateCanonicalUrl(data.url);
  const links = [];

  if (hasEditorials(data)) {
    for (const editorial of data.editorials) {
      links.push({
        tagName: 'link',
        rel: 'alternate',
        type: 'application/rss+xml',
        href: getEditorialHref(baseUrl, editorial.slug)
      });
    }
  } else {
    links.push({
      tagName: 'link',
      rel: 'alternate',
      type: 'application/rss+xml',
      href: getDefaultHref(baseUrl)
    });
  }

  return [
    { title: `${data.article.homeTitle} | Folha Dirigida` },
    { name: 'robots', content: 'max-image-preview:large' },
    { name: 'description', content: data.article.metadataConfig },
    { name: 'url', content: data.url },
    { name: 'name', content: data.article.title },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    {
      property: 'og:title',
      content: data.article.title
    },
    {
      property: 'og:type',
      content: 'website'
    },
    {
      property: 'og:description',
      content: data.article.description
    },
    {
      property: 'og:image',
      content: `${data.article.image}`
    },
    {
      property: 'og:image:width',
      content: '1200'
    },
    {
      property: 'og:image:height',
      content: '1200'
    },
    {
      property: 'og:url',
      content: data.url
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image'
    },
    {
      name: 'twitter:site',
      content: '@folhadirigida'
    },
    {
      name: 'twitter:creator',
      content: `${data.article.author}`
    },
    {
      name: 'twitter:url',
      content: data.url
    },
    {
      name: 'twitter:title',
      content: data.article.title
    },
    {
      name: 'twitter:description',
      content: data.article.description
    },
    {
      name: 'twitter:image',
      content: `${data.article.image}`
    },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Início',
            item: data.baseUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Editoria',
            item: `${data.baseUrl}/n`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: getFirstWords(data.article.title, 2)
          }
        ]
      }
    },
    ...links
  ];
};

const CACHE_CONTROL =
  'public, max-age=600, s-maxage=1800, stale-while-revalidate=600';

export const headers = () => ({
  'Cache-Control': CACHE_CONTROL
});

export const loader = async ({
  params,
  request,
  context
}: Route.LoaderArgs) => {
  const url = new URL(request.url);
  const slug = params.slug as string;
  const articlesService = new ArticlesService();
  const currentUser = context.currentUser;

  const loadAllResources = async () => {
    const articlePromise = articlesService.getAtreusArticle({
      articleSlug: slug
    });

    const sidebarPromise = articlesService
      .getAtreusV2ArticlesSidebar({
        articleSlug: slug
      })
      .catch(() => ({}) as Record<string, unknown>);

    const article = await articlePromise;

    const commentariesPromise = currentUser
      ? getCommentaries(article.article.id, 1)
      : Promise.resolve({ commentaries: [], meta: { hasMore: false } });

    const contentProcessingPromise = new Promise<{
      firstPart: string;
      secondPart: string;
    }>(resolve => {
      const { firstPart, secondPart } = splitContentByParagraphs(
        article.article.content,
        3
      );

      resolve({
        firstPart: addingShortcodeContent(firstPart),
        secondPart: addingShortcodeContent(secondPart)
      });
    });

    const [sidebar, getCommentariesResponse, processedContent] =
      await Promise.all([
        sidebarPromise,
        commentariesPromise,
        contentProcessingPromise
      ]);

    return { article, sidebar, getCommentariesResponse, processedContent };
  };

  const { article, sidebar, getCommentariesResponse, processedContent } =
    await loadAllResources();

  const baseContentGroup = 'fd news';
  const contentGroup = article.article.aiCreated
    ? `${baseContentGroup} ai`
    : `${baseContentGroup} human`;

  return data(
    {
      url: url.toString(),
      baseUrl: url.origin,
      ...article,
      sidebar,
      firstPart: processedContent.firstPart,
      secondPart: processedContent.secondPart,
      contentGroup,
      getCommentariesResponse,
      currentUser,
      articleId: article.article.id,
      userAuthenticated: !!currentUser,
      userInfo: {
        id: currentUser?.id,
        name: currentUser?.name,
        avatarUrl: currentUser?.avatarUrl
      }
    },
    {
      status: 200,
      headers: {
        'Cache-Control': CACHE_CONTROL,
        Vary: 'Cookie'
      }
    }
  );
};

export default function NewsShowPage({ loaderData }: Route.ComponentProps) {
  const {
    url,
    article,
    mostAccessed,
    articles,
    relatedNews,
    editorials,
    mostRecent,
    sidebar,
    firstPart,
    secondPart,
    banner,
    contentGroup,
    getCommentariesResponse,
    articleId,
    userAuthenticated,
    userInfo
  } = loaderData;

  useMounted(() => {
    if (window?.zaraz) {
      window.zaraz.set('content_group', contentGroup);
    }
  });

  const wasCalled = useRef<boolean>(false);

  useEffect(() => {
    /* FIXME: to run once */
    if (wasCalled.current) {
      return;
    }

    wasCalled.current = true;

    const questionTarget = document.querySelector('#question') as HTMLElement;

    if (questionTarget) {
      const root = createRoot(questionTarget);

      root.render(
        <Suspense>
          <Questions
            course={JSON.parse(article.widget as string).courses[0]}
            questions={JSON.parse(article.widget as string).questions}
            share={{ url, title: article.title }}
          />
        </Suspense>
      );
    }
  }, [article.title, article.widget, url]);

  const hasSidebar = Boolean(sidebar && Object.values(sidebar).length > 0);

  return (
    <NewsStoreProvider
      initialState={{
        isLikeActive: false,
        isDislikeActive: false,
        likes: article.likes,
        dislikes: article.dislikes,
        comments: {
          ...getCommentariesResponse
        },
        userInfo: userAuthenticated ? userInfo : undefined,
        articleId
      }}
    >
      <Page>
        <Container
          css={{
            pb: '$4',
            '@lg': {
              pb: '$2'
            }
          }}
        >
          <Stack
            css={{
              flexColCenter: !hasSidebar
            }}
            flow={{
              '@initial': 'column',
              '@lg': 'row'
            }}
            gap="$8"
          >
            <Box>
              <Box css={{ maxW: 720 }}>
                <Box
                  css={{
                    mb: '$2'
                  }}
                >
                  <Breadcrumb>
                    <BreadcrumbLink href={'/'}>Início</BreadcrumbLink>
                    <BreadcrumbLink href={'/noticias-de-concursos'}>
                      Notícias de Concursos
                    </BreadcrumbLink>
                    <BreadcrumbLink href="#" isCurrentPage>
                      {getFirstWords(article.title, 2)}
                    </BreadcrumbLink>
                  </Breadcrumb>
                </Box>
                <Box as="header">
                  <Text
                    as="h1"
                    dangerouslySetInnerHTML={{
                      __html: safeRenderHtml(article.title)
                    }}
                    variant={{
                      '@initial': 'displayXs',
                      '@lg': 'displaySm'
                    }}
                    weight="semibold"
                  />
                  {article.description && (
                    <Text
                      as="h2"
                      css={{ color: '$textMuted', my: '$4' }}
                      dangerouslySetInnerHTML={{
                        __html: safeRenderHtml(article.description)
                      }}
                      variant={{
                        '@initial': 'md',
                        '@lg': 'lg'
                      }}
                    />
                  )}
                  {article.editorials.length > 0 && (
                    <Badges items={article.editorials} />
                  )}
                  <Stack
                    css={{
                      pt: '$6',
                      gap: '$2',
                      textSm: true,
                      '@lg': {
                        gap: '$4',
                        textMd: true
                      }
                    }}
                    flow={{
                      '@initial': 'column',
                      '@sm': 'row'
                    }}
                  >
                    <Stack css={{ noWrap: true }} gap="$2">
                      <Text css={{ fontWeight: '$semibold' }}>Autor:</Text>
                      <Text>{article.author}</Text>
                    </Stack>
                    <Stack css={{ noWrap: true }} gap="$2">
                      <Text css={{ fontWeight: '$semibold' }}>
                        Publicado em:
                      </Text>
                      <Text>{article.publishedAt}</Text>
                    </Stack>
                    <Stack css={{ noWrap: true, gap: '$2' }}>
                      <Text css={{ fontWeight: '$semibold' }}>
                        Atualizado em:
                      </Text>
                      <Text>{article.updatedAt}</Text>
                    </Stack>
                  </Stack>
                  <Stack
                    css={{
                      my: '$16',
                      flexDirection: 'column',
                      gap: '$4',
                      '@lg': { flexDirection: 'row', gap: '$12' }
                    }}
                  >
                    <LikeDislikeButtons slug={article?.slug} />
                    <Box css={{ borderRight: '1px solid $separatorColor' }} />
                    <SocialShareButtons url={url} />
                  </Stack>
                </Box>
                <PageBelowTheFoldComponent
                  article={article}
                  articles={articles}
                  banner={banner}
                  editorials={editorials}
                  firstPart={firstPart}
                  mostAccessed={mostAccessed}
                  mostRecent={mostRecent}
                  relatedNews={relatedNews}
                  secondPart={secondPart}
                />
              </Box>
            </Box>
            {hasSidebar && <Aside data={sidebar as ArticleSidebar} />}
          </Stack>
        </Container>
        <script
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'NewsArticle',
              headline: article.title,
              image: [article.image],
              datePublished: article.publishedAtIso,
              dateModified: article.updatedAtIso,
              author: article.author
            })
          }}
          type="application/ld+json"
        />
      </Page>
    </NewsStoreProvider>
  );
}
